import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

const PLAN_TYPES = [
  // Basic reativado
  {
    id: 'basic',
    name: 'Basic',
    description: 'Essenciais para começar',
    features: ['1 usuário', '1 conta WhatsApp', 'Implantação sob consulta', 'Suporte por email', 'Volume de obras: até 2 obras'],
  },
  {
    id: 'advanced',
    name: 'Advanced',
    description: 'Para pequenas equipes',
    features: ['3 usuários', '1 conta WhatsApp', 'Implantação sob consulta', 'Suporte em horário comercial', 'Volume de obras: até 3 obras'],
  },
  {
    id: 'pro',
    name: 'Pro',
    description: 'Para times em crescimento',
    features: ['10 usuários', '5 contas WhatsApp', 'Implantação sob consulta', 'Suporte prioritário', 'Volume de obras: até 10 obras'],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Para operações exigentes',
    features: ['Usuários ilimitados', 'Contas WhatsApp ilimitadas', 'Implantação sob consulta', 'Suporte 24/7', 'Volume de obras: ilimitadas'],
  },
] as const;

type PlanTypeId = typeof PLAN_TYPES[number]['id'];

const MIN_VOLUME = 250;
const MAX_VOLUME = 20000; // valor ajustável
const STEP = 250;

// Tabelas de preço centralizadas (fonte única de verdade)
const BASE_BY_PLAN: Record<PlanTypeId, number> = {
  basic: 990,
  advanced: 1990,
  pro: 3990,
  enterprise: 7990,
};

const UNIT_BY_PLAN: Record<PlanTypeId, number> = {
  basic: 350,
  advanced: 300,
  pro: 200,
  enterprise: 200,
};

// Negociações incluídas por plano (mês)
const INCLUDED_BY_PLAN: Record<PlanTypeId, number> = {
  basic: 250,
  advanced: 500,
  pro: 1000,
  enterprise: 4000,
};

function toStep(value: number) {
  // ajusta para múltiplos de 250 dentro do range
  const clamped = Math.max(MIN_VOLUME, Math.min(MAX_VOLUME, value));
  const stepped = Math.round(clamped / STEP) * STEP;
  return stepped;
}

const PlanosFlex: React.FC = () => {
  const [step, setStep] = useState<1 | 2>(1);
  const [planType, setPlanType] = useState<PlanTypeId>('advanced');
  const [volume, setVolume] = useState<number>(MIN_VOLUME);
  const navigate = useNavigate();

  const selectedPlan = useMemo(() => PLAN_TYPES.find(p => p.id === planType)!, [planType]);

  // Ensure /planos-flex is not indexed by search engines
  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const prevContent = meta?.getAttribute('content') ?? null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'noindex, nofollow');

    return () => {
      if (meta) {
        if (prevContent === null) {
          document.head.removeChild(meta);
        } else {
          meta.setAttribute('content', prevContent);
        }
      }
    };
  }, []);

  // Precificação dinâmica
  const pricing = useMemo(() => {
    const includedNegotiations = INCLUDED_BY_PLAN[planType];
    const chargeableVolume = Math.max(0, volume - includedNegotiations);
    const blocks = chargeableVolume > 0 ? Math.ceil(chargeableVolume / STEP) : 0;

    // Descontos por faixa de volume (com base em blocos cobrados de 250)
    // 0-4 blocos: 0%, 5-20 blocos: 5%, 21-40 blocos: 10%, >40 blocos: 15%
    let discountPct = 0;
    if (blocks >= 5 && blocks <= 20) discountPct = 0.05;
    else if (blocks >= 21 && blocks <= 40) discountPct = 0.1;
    else if (blocks > 40) discountPct = 0.15;

    const base = BASE_BY_PLAN[planType];
    const unit = UNIT_BY_PLAN[planType];
    const usage = blocks * unit; // cobrança por blocos APÓS inclusão
    // Desconto aplicado apenas sobre o uso variável
    const usageDiscount = usage * discountPct;
    const subtotal = base + usage;
    const total = base + (usage - usageDiscount);

    return {
      base,
      unit,
      includedNegotiations,
      chargeableVolume,
      blocks,
      discountPct,
      usage,
      usageDiscount,
      subtotal,
      total,
    };
  }, [planType, volume]);

  // Persistência da seleção
  useEffect(() => {
    // Restaurar seleção anterior, se houver
    const stored = sessionStorage.getItem('flexPlanSelection');
    if (stored) {
      try {
        const data = JSON.parse(stored);
        if (data?.planType) setPlanType(data.planType);
        if (data?.volume) setVolume(data.volume);
        if (data?.step) setStep(data.step);
      } catch {}
    }
  }, []);


  useEffect(() => {
    // Salvar seleção atual
    const includedNegotiations = INCLUDED_BY_PLAN[planType];
    const chargeableVolume = Math.max(0, volume - includedNegotiations);
    const data = { planType, volume, step, includedNegotiations, chargeableVolume };
    sessionStorage.setItem('flexPlanSelection', JSON.stringify(data));
  }, [planType, volume, step]);

  const handleProceedToPayment = () => {
    // Mapear números de recursos baseado no tipo do plano (estimados dos recursos listados)
    const whatsappByPlan: Record<PlanTypeId, number> = {
      basic: 1,
      advanced: 1,
      pro: 5,
      enterprise: 10,
    };
    const usersByPlan: Record<PlanTypeId, number> = {
      basic: 1,
      advanced: 3,
      pro: 10,
      enterprise: 50,
    };

    const customerId = sessionStorage.getItem('asaasCustomerId') || '';
    const firebaseCustomerId = sessionStorage.getItem('firebaseCustomerId') || '';
    const customerDataRaw = sessionStorage.getItem('customerData');
    const customerData = customerDataRaw ? JSON.parse(customerDataRaw) : undefined;

    const includedNegotiations = INCLUDED_BY_PLAN[planType];
    const chargeableVolume = Math.max(0, volume - includedNegotiations);
    const checkoutData = {
      // Campos esperados por Pagamento.tsx
      plan: `Flex-${selectedPlan.name}`,
      planName: `${selectedPlan.name} Flex (${volume.toLocaleString('pt-BR')} negociações/mês)`,
      whatsappAccounts: whatsappByPlan[planType],
      extraWhatsappAccounts: 0,
      negotiationsIncluded: volume,
      extraNegotiationsPacks: 0,
      basePrice: pricing.base,
      extraWhatsappCost: 0,
      // Mapear uso (por blocos) como custo variável (antes do desconto)
      extraNegotiationsCost: pricing.usage,
      totalPrice: pricing.total,
      flexPlan: {
        planType,
        includedNegotiations,
        chargeableVolume,
        unitPricePer250: UNIT_BY_PLAN[planType],
        blocks: pricing.blocks,
        discountPct: pricing.discountPct,
      },
      customerId,
      firebaseCustomerId,
      customerData,
    };

    // Persistir e navegar para pagamento
    sessionStorage.setItem('checkoutData', JSON.stringify(checkoutData));
    navigate('/pagamento', { state: checkoutData });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="container mx-auto px-4 pt-10 pb-20">
        <div className="max-w-5xl mx-auto"></div>
          <header className="text-center mb-12">
            <span className="inline-block text-brand-roxo font-semibold text-sm uppercase tracking-wider mb-3">
              Plano Flex
            </span>
            <h1 className="font-display text-3xl md:text-5xl font-extrabold text-brand-azul leading-tight">
              Monte seu plano em duas etapas
            </h1>
            <p className="text-brand-azul/70 mt-3 max-w-xl mx-auto">
              Escolha o tipo de plano e o volume de negociações. O preço acompanha a sua operação.
            </p>
          </header>

          {/* Stepper */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className={cn('h-10 px-4 rounded-full flex items-center gap-2', step === 1 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')}>
              <span className="text-sm font-medium">1</span>
              <span className="text-sm">Tipo de plano</span>
            </div>
            <div className="h-px w-10 bg-muted-foreground/20" />
            <div className={cn('h-10 px-4 rounded-full flex items-center gap-2', step === 2 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground')}>
              <span className="text-sm font-medium">2</span>
              <span className="text-sm">Volume de negociações</span>
            </div>
          </div>

          {step === 1 && (
            <section>
              <Card>
                <CardHeader>
                  <CardTitle>Escolha o tipo de plano</CardTitle>
                  <CardDescription>Mesmo look-and-feel de /planos, com cartões lado a lado e destaque do plano recomendado.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4">
                    {PLAN_TYPES.map((plan) => {
                      const popular = plan.id === 'pro';
                      const selected = planType === plan.id;
                      const formatPrice = (v: number) => v.toLocaleString('pt-BR');
                      return (
                        <div
                          key={plan.id}
                          className={cn('relative plan-card cursor-pointer', selected ? 'ring-2 ring-primary' : 'hover:shadow-md')}
                          onClick={() => setPlanType(plan.id)}
                        >
                          {popular && (
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brand-roxo text-white font-bold py-1 px-3 rounded-full text-xs uppercase tracking-wider">
                              Recomendado
                            </div>
                          )}
                          <h3 className="font-display text-xl md:text-2xl font-bold text-brand-azul mb-2">{plan.name}</h3>
                          <p className="text-brand-azul/70 mb-4 min-h-[50px]">{plan.description}</p>
                          {plan.id === 'enterprise' ? (
                            <div className="price-tag mb-4">Sob Consulta</div>
                          ) : (
                            <div className="price-tag mb-4">R$ {formatPrice(BASE_BY_PLAN[plan.id])} <small>/mês</small></div>
                          )}

                          <div className="feature-list my-6">
                            {plan.id === 'enterprise' ? (
                              <div className="text-sm text-muted-foreground">Sob Consulta</div>
                            ) : (
                              <ul className="space-y-2 text-sm text-muted-foreground list-disc list-inside">
                                <li key="includedNegotiations">Inclui {INCLUDED_BY_PLAN[plan.id].toLocaleString('pt-BR')} negociações/mês</li>
                                {plan.features.map((f) => (
                                  <li key={f}>{f}</li>
                                ))}
                              </ul>
                            )}
                          </div>

                          <Button
                            className={cn('w-full py-2.5 rounded-full font-semibold flex items-center justify-center gap-2 mt-auto',
                              popular ? 'bg-brand-roxo hover:bg-brand-roxo/90 text-white' : 'bg-brand-azul hover:bg-brand-azul/90 text-white'
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              setPlanType(plan.id);
                              setStep(2);
                            }}
                          >
                            Selecionar {plan.name}
                          </Button>
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex justify-end mt-6">
                    <Button size="lg" onClick={() => setStep(2)} disabled={!planType}>Continuar</Button>
                  </div>
                </CardContent>
              </Card>
            </section>
          )}

          {step === 2 && (
            <section className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Defina o volume de negociações</CardTitle>
                  <CardDescription>Use o controle deslizante ou digite um número múltiplo de {STEP}. Intervalo: {MIN_VOLUME} a {MAX_VOLUME}.</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <Label className="mb-2 block">Negociações por mês</Label>
                      <Slider
                        value={[volume]}
                        min={MIN_VOLUME}
                        max={MAX_VOLUME}
                        step={STEP}
                        onValueChange={(v) => setVolume(v[0])}
                      />
                      <div className="mt-3 text-sm text-muted-foreground">Selecionado: {volume.toLocaleString('pt-BR')}</div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                      <div>
                        <Label htmlFor="volumeInput" className="mb-2 block">Ou digite o volume</Label>
                        <Input
                          id="volumeInput"
                          inputMode="numeric"
                          value={volume}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/\D/g, '');
                            const num = raw ? parseInt(raw, 10) : MIN_VOLUME;
                            setVolume(num);
                          }}
                          onBlur={() => setVolume(toStep(volume))}
                          placeholder={`Múltiplo de ${STEP}`}
                        />
                        <p className="mt-2 text-xs text-muted-foreground">Será ajustado automaticamente para o múltiplo de {STEP} mais próximo entre {MIN_VOLUME} e {MAX_VOLUME}.</p>
                      </div>

                      <div className="p-4 border rounded-lg">
                        <div className="text-sm text-muted-foreground">Resumo</div>
                        {selectedPlan.id === 'enterprise' ? (
                          <div className="mt-1 text-base font-medium">{selectedPlan.name} • Sob Consulta</div>
                        ) : (
                          <>
                            <div className="mt-1 text-base font-medium">{selectedPlan.name} • {volume.toLocaleString('pt-BR')} negociações/mês</div>
                            <ul className="mt-3 space-y-1 text-sm text-muted-foreground list-disc list-inside">
                              {selectedPlan.features.map((f) => (
                                <li key={f}>{f}</li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>

                      <div className="p-4 border rounded-lg bg-muted/30">
                        <div className="text-sm text-muted-foreground">Precificação</div>
                        {selectedPlan.id === 'enterprise' ? (
                          <div className="mt-2 text-sm">Sob Consulta</div>
                        ) : (
                          <div className="mt-2 space-y-1 text-sm">
                            <div className="flex justify-between">
                              <span>Mensalidade base ({selectedPlan.name})</span>
                              <span>R$ {pricing.base.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Inclusas no plano</span>
                              <span>{pricing.includedNegotiations.toLocaleString('pt-BR')} negociações</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Uso cobrado: {pricing.blocks} × {STEP} negociações</span>
                              <span>R$ {pricing.usage.toFixed(2)} <span className="text-muted-foreground">(R$ {pricing.unit.toFixed(2)}/250)</span></span>
                            </div>
                            <div className="flex justify-between">
                              <span>Subtotal</span>
                              <span>R$ {pricing.subtotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Desconto por volume {pricing.discountPct > 0 ? `(${(pricing.discountPct*100).toFixed(0)}%)` : ''}</span>
                              <span>- R$ {pricing.usageDiscount.toFixed(2)}</span>
                            </div>
                            <div className="h-px bg-border my-1" />
                          <div className="flex justify-between font-semibold">
                            <span>Total mensal</span>
                            <span>R$ {pricing.total.toFixed(2)}</span>
                          </div>
                        </div>
                        )}
                      </div>

                      </div>

                      <div className="flex items-center justify-between">
                        <Button variant="outline" onClick={() => setStep(1)}>Voltar</Button>
                        <Button size="lg" onClick={handleProceedToPayment}>
                          {selectedPlan.id === 'enterprise' ? 'Concluir seleção • Sob Consulta' : `Concluir seleção • R$ ${pricing.total.toFixed(2)}/mês`}
                        </Button>
                      </div>
                  </div>
                </CardContent>
              </Card>
            </section>
          )}
      </main>
      <Footer />
    </div>
  );
};
export default PlanosFlex;
